// src/components/EmailSignatureGenerator.jsx
import { useState } from 'react';
import { X, Copy, Check } from 'lucide-react';
import styles from './EmailSignatureGenerator.module.css';

const API_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';
const BADGE_API_URL = import.meta.env.VITE_PRODUCTION_API_URL || 'https://api.vennio.app';
const APP_URL = import.meta.env.VITE_APP_URL || 'http://localhost:5173';

export default function EmailSignatureGenerator({ vennLink, businessId, onClose }) {
  const [copied, setCopied] = useState(false);
  const [config, setConfig] = useState({
    style: 'professional',
    size: 'medium',
    color: '5B21B6', // Purple/indigo
    theme: 'light',
    type: 'booking'  // 'booking' or 'access-request'
  });

  const STYLES = [
    { value: 'minimal',      label: 'Minimal',      description: 'Simple and clean' },
    { value: 'professional', label: 'Professional',  description: 'Business-ready' },
    { value: 'branded',      label: 'Branded',       description: 'Full branding' }
  ];

  const SIZES = [
    { value: 'small',  label: 'Small',  description: '200x50' },
    { value: 'medium', label: 'Medium', description: '300x75' },
    { value: 'large',  label: 'Large',  description: '400x100' }
  ];

  const COLORS = [
    { value: '5B21B6', label: 'Purple', hex: '#5B21B6' },
    { value: '2563EB', label: 'Blue',   hex: '#2563EB' },
    { value: '059669', label: 'Green',  hex: '#059669' },
    { value: 'DC2626', label: 'Red',    hex: '#DC2626' },
    { value: 'EA580C', label: 'Orange', hex: '#EA580C' },
    { value: '7C3AED', label: 'Violet', hex: '#7C3AED' }
  ];

  const THEMES = [
    { value: 'light', label: 'Light' },
    { value: 'dark',  label: 'Dark' }
  ];

  const WIDGET_TYPES = [
    { value: 'booking',        label: 'Booking Badge',        description: 'Let people book meetings with you',              icon: '📅' },
    { value: 'access-request', label: 'Access Request Badge', description: 'Let people grant you access to their calendar',  icon: '🔓' }
  ];

  const previewBadgeUrl = `${API_URL}/api/v1/badge/${vennLink.short_code}?style=${config.style}&color=${config.color}&size=${config.size}&theme=${config.theme}&type=${config.type}`;
  const badgeUrl        = `${BADGE_API_URL}/api/v1/badge/${vennLink.short_code}?style=${config.style}&color=${config.color}&size=${config.size}&theme=${config.theme}&type=${config.type}`;
  const targetUrl = config.type === 'booking'
    ? `${APP_URL}/v/${vennLink.short_code}?ref=email`
    : `${APP_URL}/access-request/${businessId}?ref=email&name=${encodeURIComponent(vennLink.title || 'Calendar Owner')}`;

  const generateHtml = () => {
    const altText = config.type === 'booking'
      ? 'Schedule a meeting with me'
      : 'Grant me access to your calendar';

    return `<table cellpadding="0" cellspacing="0" border="0" style="margin: 10px 0;">
  <tr>
    <td>
      <a href="${targetUrl}" style="text-decoration: none; display: inline-block;">
        <img src="${badgeUrl}"
             alt="${altText}"
             style="border: 0; display: block; max-width: 100%; height: auto;" />
      </a>
    </td>
  </tr>
</table>`;
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(generateHtml());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        {/* Header */}
        <div className={styles.modalHeader}>
          <div className={styles.modalHeaderLeft}>
            <h2 className={styles.modalTitle}>
              <svg className={styles.titleIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              Email Signature Badge
            </h2>
            <p className={styles.modalSubtitle}>Transform every email into a booking opportunity</p>
          </div>
          <button onClick={onClose} className={styles.closeBtn}><X size={24} /></button>
        </div>

        {/* Body */}
        <div className={styles.modalBody}>
          {/* Left: Customization */}
          <div className={styles.customizeCol}>
            <h3 className={styles.sectionTitle}>Customize Your Badge</h3>

            {/* Widget Type */}
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Widget Type</label>
              <div className={styles.typeGrid}>
                {WIDGET_TYPES.map((type) => (
                  <button
                    key={type.value}
                    onClick={() => setConfig({ ...config, type: type.value })}
                    className={`${styles.typeBtn}${config.type === type.value ? ` ${styles.typeBtnActive}` : ''}`}
                  >
                    <span className={styles.typeIcon}>{type.icon}</span>
                    <div>
                      <div className={styles.typeLabel}>{type.label}</div>
                      <div className={styles.typeDesc}>{type.description}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Style */}
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Badge Style</label>
              <div className={styles.optionGrid3}>
                {STYLES.map((s) => (
                  <button
                    key={s.value}
                    onClick={() => setConfig({ ...config, style: s.value })}
                    className={`${styles.optionBtn}${config.style === s.value ? ` ${styles.optionBtnActive}` : ''}`}
                  >
                    <div className={styles.optionLabel}>{s.label}</div>
                    <div className={styles.optionDesc}>{s.description}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Size */}
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Badge Size</label>
              <div className={styles.optionGrid3}>
                {SIZES.map((s) => (
                  <button
                    key={s.value}
                    onClick={() => setConfig({ ...config, size: s.value })}
                    className={`${styles.optionBtn}${config.size === s.value ? ` ${styles.optionBtnActive}` : ''}`}
                  >
                    <div className={styles.optionLabel}>{s.label}</div>
                    <div className={styles.optionDesc}>{s.description}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Color */}
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Badge Color</label>
              <div className={styles.colorGrid}>
                {COLORS.map((c) => (
                  <button
                    key={c.value}
                    onClick={() => setConfig({ ...config, color: c.value })}
                    className={`${styles.colorSwatch}${config.color === c.value ? ` ${styles.colorSwatchActive}` : ''}`}
                    title={c.label}
                  >
                    <div className={styles.colorSwatchInner} style={{ backgroundColor: c.hex }} />
                    {config.color === c.value && (
                      <div className={styles.colorCheck}>
                        <Check size={20} className={styles.colorCheckIcon} />
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Theme */}
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Theme</label>
              <div className={styles.optionGrid2}>
                {THEMES.map((t) => (
                  <button
                    key={t.value}
                    onClick={() => setConfig({ ...config, theme: t.value })}
                    className={`${styles.optionBtn}${config.theme === t.value ? ` ${styles.optionBtnActive}` : ''}`}
                  >
                    <div className={`${styles.optionLabel} ${styles.optionLabelCenter}`}>{t.label}</div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Right: Preview & Copy */}
          <div className={styles.previewCol}>
            <h3 className={styles.sectionTitle}>Live Preview</h3>

            <div className={styles.previewArea}>
              <img
                src={previewBadgeUrl}
                alt="Email signature badge preview"
                className={styles.previewImg}
                style={{ maxWidth: '100%', height: 'auto' }}
              />
            </div>

            <button
              onClick={copyToClipboard}
              className={`${styles.copyBtn}${copied ? ` ${styles.copyBtnCopied}` : ''}`}
            >
              {copied ? (
                <><Check size={20} />Copied to Clipboard!</>
              ) : (
                <><Copy size={20} />Copy HTML for Email Signature</>
              )}
            </button>

            <div className={styles.codeSection}>
              <label className={styles.formLabel}>HTML Code</label>
              <pre className={styles.codePre}><code>{generateHtml()}</code></pre>
            </div>

            {/* Setup Instructions */}
            <div className={styles.infoBox}>
              <h4 className={styles.infoBoxTitle}>Setup Instructions</h4>
              <div className={styles.infoBoxContent}>
                <div>
                  <strong>Gmail:</strong>
                  <ol className={styles.infoBoxList}>
                    <li>Click Settings (gear icon) → See all settings</li>
                    <li>Scroll to "Signature" section</li>
                    <li>Click "Insert image" icon, then "Web Address (URL)"</li>
                    <li>Paste the image URL from above</li>
                    <li>Right-click image → Link → paste booking URL</li>
                  </ol>
                </div>
                <div>
                  <strong>Outlook:</strong>
                  <ol className={styles.infoBoxList}>
                    <li>File → Options → Mail → Signatures</li>
                    <li>Click "Insert Picture" icon</li>
                    <li>Use "From a web address" and paste image URL</li>
                    <li>Select image → Insert Hyperlink → paste booking URL</li>
                  </ol>
                </div>
                <div>
                  <strong>Apple Mail:</strong>
                  <ol className={styles.infoBoxList}>
                    <li>Mail → Settings → Signatures</li>
                    <li>Create new signature</li>
                    <li>Copy the HTML above and paste into signature editor</li>
                  </ol>
                </div>
              </div>
            </div>

            {/* Widget type info */}
            {config.type === 'booking' ? (
              <div className={styles.typeInfoBox}>
                <h4 className={styles.typeInfoBoxTitle}>📅 Booking Badge</h4>
                <p className={styles.typeInfoBoxText}>
                  Recipients can click to view your availability and book meetings directly.
                  Includes <code className={styles.inlineCode}>?ref=email</code> for attribution tracking.
                </p>
              </div>
            ) : (
              <div className={styles.typeInfoBox}>
                <h4 className={styles.typeInfoBoxTitle}>🔓 Access Request Badge</h4>
                <p className={styles.typeInfoBoxText}>
                  Recipients can click to grant YOU access to THEIR calendar. You'll be notified when they approve,
                  allowing you to see their availability and proactively suggest meeting times.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className={styles.modalFooter}>
          <button onClick={onClose} className={styles.doneBtn}>Done</button>
        </div>
      </div>
    </div>
  );
}
